import { createSelector } from '@reduxjs/toolkit';
import { RootState } from 'app/store';
import {
  CTP_ELGIBILITY_DETAILS,
  ULTIMATE_PLAN_TIER,
  WGS_FEATURE_CODE,
  WGS_RETAIL_CC_BILLED,
  WGS_USER_ELIGIBLE_REASON_CODE,
  WGS_CTP_UPSELL_OFFER_TYPE,
  WGS_DTC_GROUP_TYPE,
  PLAN_TIER_ORDER,
  PRIVACY_ASSIST_PLAN_CODES,
  COMPANION_PLAN_CODE,
} from 'utils/constants';
import { isPastSetupWindow } from 'utils';
import { getFromBrandConfig } from 'brand-config';
import {
  selectCompanyType,
  selectCurrentPlanFamily,
  selectOfferBillingType,
  selectOfferCustomerBilled,
  selectOfferPlanCode,
  selectOfferPlanFeatures,
  selectOfferPlanTier,
} from 'services/offer/offerSelectors';
import { selectHasWhiteGloveService } from 'services/session/sessionSelectors';
import {
  selectShowConvertedToPaidBilling,
  selectUserSince,
  selectUserVendorEmployeeId,
} from 'services/user/userSelectors';
import { selectBrandCode } from 'services/brand/brandSelectors';

const selectCtpState = (state: RootState) => state.ctp;

export const selectIsExpiryNoticeModalClosed = createSelector(
  [selectCtpState],
  (ctpState) => ctpState.isExpiryNoticeModalClosed,
);

export const selectIsFinishAccountSetupModalClosed = createSelector(
  [selectCtpState],
  (ctpState) => ctpState.isFinishAccountSetupModalClosed,
);

export const selectConversionEligibility = createSelector(
  [selectCtpState],
  (ctpState) => ctpState.conversionEligibility,
);

export const selectEligibilityReasonCode = createSelector(
  [selectCtpState],
  (ctpState) => ctpState.eligibility?.reasonCode,
);

export const selectIsConversionEligibilityDetailsExist = createSelector(
  [selectConversionEligibility],
  (conversionEligibility) => conversionEligibility !== null,
);

export const selectIsEligibilityDetailsExist = createSelector(
  [selectCtpState],
  (ctpState) => ctpState.eligibility !== null,
);

export const selectAvailableOffers = createSelector(
  [selectCtpState],
  (ctpState) => ctpState.availableOffers ?? [],
);

export const selectAvailableCtpPlan = createSelector(
  [selectCtpState],
  (ctpState) => ctpState.availableCtpPlan,
);

export const selectIsEligibleForCtp = createSelector(
  [selectConversionEligibility, selectAvailableCtpPlan],
  (conversionEligibility, availableCtpPlan) =>
    (conversionEligibility?.eligible ?? false) && !!availableCtpPlan,
);

/**
 * Angular parity — `critical-notifications.component.ts:126-138
 * checkProprietaryUser` + `dashboard-main.component.ts:565`.
 *
 * True when a Discover-employee-program user is still on the legacy
 * proprietary card (no Zuora billing account created yet). Fires
 * both the DISCOVER_UPDATE_CC critical banner and the auto-open of
 * the Card Expiry Reminder modal on dashboard mount — both prompt
 * the user to migrate to a real Zuora account with a card on file.
 */
const DISCOVER_BRAND_CODE = 'discover';

/**
 * Angular parity — `my-account.service.ts:215, 279`. Discover-employee
 * gate (brand=discover + a vendorEmployeeId on the user record) —
 * used by the subscription-info and billing-history thunks to switch
 * to the `/client/discover/*` URLs, and by the billing-history row
 * renderer to pick `closedAt` over `createdAt`. Does NOT require the
 * Zuora account to be missing — that is the tighter proprietary-
 * update gate below.
 */
export const selectIsDiscoverEmployee = createSelector(
  [(state: RootState) => state.brand.code, selectUserVendorEmployeeId],
  (brandCode, vendorEmployeeId) =>
    brandCode === DISCOVER_BRAND_CODE && !!vendorEmployeeId,
);

/**
 * "Effective billable" — treat a Discover-employee proprietary user
 * as billed even when the user record's `billable` flag is false.
 * They are billed, just through the Discover employee-program card,
 * so the my-plan surfaces that key their layout on `userBillable`
 * (BillingCard's Keep-My-Plan vs Complete-My-Setup branch,
 * ExpirationCard's "Expires on" suppression, BillingHistory's card
 * visibility) must not route them onto the complimentary path.
 *
 * Angular doesn't hit this because its billing UI keys on
 * `offer.billingType` rather than the user's `billable` flag — the
 * proprietary user's offer is `retail cc billed`, and the Discover
 * branch never has to be added. React's stricter `billable` gate
 * needs this compensating selector.
 */
/* "Has a Zuora payment method on file right now." After a
 * successful CC add — standard OR Discover proprietary — the save
 * thunk internally re-fetches billing account info, so
 * `defaultPaymentMethodId` is populated the instant the save
 * resolves. Reading it lets the paid-flow UI flip immediately
 * without depending on the BE having flipped `user.billable` yet
 * (which it may not have by the time the caller re-renders). */
const selectHasZuoraPaymentMethod = createSelector(
  [(state: RootState) => state.ctp.billingAccountInfo],
  (billingAccountInfo) => Boolean(billingAccountInfo?.defaultPaymentMethodId),
);

/* Reuses `selectShowConvertedToPaidBilling` (billable OR
 * `status === 'subscriber'`) so a paying member whose `billable`
 * flag hasn't been flipped yet — a real edge case seen for some
 * Discover users where the offer is `retail cc billed` and status is
 * `subscriber`, but `billable: false` — still resolves to the paid
 * flow instead of being routed into CompleteMySetup. Discover
 * employee-program users continue to bypass via `isDiscoverEmployee`.
 *
 * Also honours `hasZuoraPaymentMethod` so the my-plan surfaces flip
 * to the paid layout the moment a first-time CC add resolves,
 * without waiting for the BE to flip `user.billable`. This matters
 * for both standard and Discover proprietary saves — for Discover,
 * `clearVendorEmployeeId` alone would otherwise route the user
 * through the false branch until a full re-login. */
export const selectIsEffectivelyBillable = createSelector(
  [selectShowConvertedToPaidBilling, selectIsDiscoverEmployee],
  (showConvertedToPaid, isDiscoverEmployee) =>
    showConvertedToPaid || isDiscoverEmployee,
);

/**
 * Paid-billing UI gate for the KeepMyPlan / ConvertedToPaid surfaces.
 * Composes `selectShowConvertedToPaidBilling` with the Discover
 * employee-program bypass so Discover proprietary users see the
 * "Recurring billing until membership is canceled. Next billing
 * date…" copy their subscription entitles them to. Also honours
 * `hasZuoraPaymentMethod` so the ConvertedToPaid body renders the
 * moment a first-time CC add resolves — Angular flips immediately
 * because it keys off `offer.billingType` (which the same fetch
 * refreshes); the payment-method flag is the equivalent React
 * signal, present as soon as `fetchBillingAccountInfo` returns.
 * Kept here rather than in `userSelectors` to avoid a circular
 * import into ctp.
 */
export const selectShowConvertedToPaidBillingEffective = createSelector(
  [
    selectShowConvertedToPaidBilling,
    selectIsDiscoverEmployee,
    selectHasZuoraPaymentMethod,
  ],
  (baseShow, isDiscoverEmployee) => baseShow || isDiscoverEmployee,
);

export const selectIsDiscoverProprietaryUpdateNeeded = createSelector(
  [
    selectIsDiscoverEmployee,
    (state: RootState) => state.ctp.billingAccountInfo,
  ],
  (isDiscoverEmployee, billingAccountInfo) =>
    isDiscoverEmployee && billingAccountInfo?.accountFound === false,
);

export const selectBillingAccountDetails = createSelector(
  [selectCtpState],
  (ctpState) => ctpState.billingAccountInfo,
);

export const selectIsBillingAccountDetailsExist = createSelector(
  [selectBillingAccountDetails],
  (billingAccountDetails) => billingAccountDetails !== null,
);

export const selectIsAvailableOffersFetched = createSelector(
  [selectCtpState],
  (ctpState) => ctpState.isAvailableOffersFetched,
);

// White Glove Service upsell banner gating — ports Angular's dashboard-carousel
// condition (dashboard-carousel.component.ts `shouldShowWgsUpsell`):
//
//   ( (currentProduct.group.type === 'DTC' && offer.billingType === RETAIL_CC_BILLED)
//     || isEligibleToShownCompleteSetupInfo(user.createdAt) )   // outer gate
//   && current product lacks id_theft_resolution
//   && some available offer plan has id_theft_resolution
//   && ( productChangeEligibility.reasonCode === 'USER_ELIGIBLE'
//        || offerChangeConfiguration.type === 'ctp upsell' )
//
// Available offers + product-change eligibility live in the ctp slice (fetched at
// bootstrap via useSessionInitialSetup).
export const selectIsEligibleForWhiteGloveBanner = createSelector(
  [
    selectHasWhiteGloveService,
    selectAvailableOffers,
    selectIsAvailableOffersFetched,
    selectCompanyType,
    selectOfferBillingType,
    selectOfferPlanFeatures,
    selectEligibilityReasonCode,
    selectUserSince,
  ],
  (
    hasWhiteGloveService,
    availableOffers,
    isAvailableOffersFetched,
    companyType,
    billingType,
    currentPlanFeatures,
    eligibilityReasonCode,
    userSince,
  ) => {
    if (!isAvailableOffersFetched || hasWhiteGloveService) {
      return false;
    }

    const isWithinSetupOrRetailWindow =
      (companyType === WGS_DTC_GROUP_TYPE &&
        billingType === WGS_RETAIL_CC_BILLED) ||
      isPastSetupWindow(userSince);

    if (!isWithinSetupOrRetailWindow) {
      return false;
    }

    const currentProductHasWgs =
      currentPlanFeatures?.includes(WGS_FEATURE_CODE) ?? false;
    if (currentProductHasWgs) {
      return false;
    }

    const isUserEligible =
      eligibilityReasonCode === WGS_USER_ELIGIBLE_REASON_CODE;

    return availableOffers.some(
      (o) =>
        o.plan?.features?.includes(WGS_FEATURE_CODE) &&
        (isUserEligible ||
          o.offerChangeConfiguration?.type === WGS_CTP_UPSELL_OFFER_TYPE),
    );
  },
);

// Brands whose CTP flow supports breach-enrollment upsells. Ports
// Angular `product.service.ts:208-210` (`isBreachEnrollmentSupported`).
const BREACH_ENROLLMENT_BRANDS = new Set(['mshield', 'afs2', 'iddefense']);

/**
 * "Protect your entire family" carousel banner gating — ports Angular's
 * dashboard-carousel `checkCtpFamUpsellBanner` (`dashboard-carousel
 * .component.ts:158-196` + `product.service.ts:294-302`).
 *
 * Fires when EVERY condition holds:
 *   • brand supports breach enrollment (mshield / afs2 / iddefense)
 *   • current product group is BREACH
 *   • current offer billing type is `complimentary` (CTP user)
 *   • account is past the 90-day setup window
 *   • available offers include one with type `ctp upsell family`
 *
 * The offer-type check is separate from `selectIsEligibleForWhiteGlove
 * Banner` because Angular treats these as two distinct upsells — the
 * WGS banner reads `ctp upsell` only, the family banner reads
 * `ctp upsell family` only. A user mapped only to CTP_UPSELL_FAMILY
 * will trigger this banner but NOT the WGS banner (parity with
 * Angular's dashboard carousel behaviour).
 */
export const selectIsEligibleForCtpFamilyUpsellBanner = createSelector(
  [
    selectBrandCode,
    selectCompanyType,
    selectOfferBillingType,
    selectAvailableOffers,
    selectIsAvailableOffersFetched,
    selectUserSince,
  ],
  (
    brandCode,
    companyType,
    billingType,
    availableOffers,
    isAvailableOffersFetched,
    userSince,
  ) => {
    if (!isAvailableOffersFetched) return false;
    if (!BREACH_ENROLLMENT_BRANDS.has(brandCode)) return false;
    if (companyType !== 'breach') return false;
    if (billingType !== 'complimentary') return false;
    if (!isPastSetupWindow(userSince)) return false;
    return availableOffers.some(
      (o) =>
        (o.offerChangeConfiguration?.type ?? '').toLowerCase() ===
        'ctp upsell family',
    );
  },
);

const isUltimateTier = (tier?: string) =>
  (tier ?? '').trim().toLowerCase() === ULTIMATE_PLAN_TIER;

/* Brands predating this key inherit `default`, which disables every banner. */
const getUpgradeBannerConfig = () =>
  getFromBrandConfig('planUpgradeBanners') ?? {
    ultimate: false,
    family: false,
    requireRetailDtc: true,
  };

/**
 * Shared offer gate for the three plan-upgrade carousel banners — ports the
 * outer condition Angular wraps them in
 * (`dashboard-carousel.component.ts:134-136`, repeated at `:152`).
 *
 * Which brands qualify, and whether they must additionally be a DTC group on a
 * retail-cc-billed offer, is declared per brand under `planUpgradeBanners`
 * (`brand-config/types.ts`) rather than compared here — Angular's hard-coded
 * `'boapa'` / `'iddefense'` branches become config entries.
 */
const selectSatisfiesUpgradeBannerOfferGate = createSelector(
  [selectCompanyType, selectOfferBillingType],
  (companyType, billingType) => {
    if (!getUpgradeBannerConfig().requireRetailDtc) return true;

    return (
      companyType === WGS_DTC_GROUP_TYPE && billingType === WGS_RETAIL_CC_BILLED
    );
  },
);

const selectIsProductChangeEligible = createSelector(
  [selectEligibilityReasonCode],
  (reasonCode) => reasonCode === WGS_USER_ELIGIBLE_REASON_CODE,
);

const tierRank = (tier?: string) =>
  PLAN_TIER_ORDER.indexOf((tier ?? '').trim().toLowerCase());

/**
 * "Level up your identity theft protection with more features." — the upsell
 * banner in the critical-notification slot. Ports Angular
 * `productService.canWeShowViewOffers` (`product.service.ts:118-122`) together
 * with the brand gate its only caller applies
 * (`critical-notifications.component.ts:105-118`):
 *
 *   brandCode !== 'boapa' && brandCode !== 'iddefense'
 *   && product.offer.customerBilled
 *   && productChangeEligibility.reasonCode === 'USER_ELIGIBLE'
 *   && checkUpSellPlanAvailable(ps)
 *
 * The brand comparison becomes the `upsellBanner` brand-config flag; `default`
 * enables it so the config states Angular's exclusion rather than inverting it.
 *
 * Session-scoped dismissal (`sessionStorage 'dismissUpsellBanner'`) is not part
 * of this selector — it is view state, and lives with the banner itself the same
 * way `useCardExpiryReminder` owns its own skip key.
 */
export const selectIsEligibleForUpsellBanner = createSelector(
  [
    selectIsAvailableOffersFetched,
    selectAvailableOffers,
    selectOfferCustomerBilled,
    selectIsProductChangeEligible,
    selectOfferPlanCode,
    selectOfferPlanTier,
    selectCurrentPlanFamily,
  ],
  (
    isAvailableOffersFetched,
    availableOffers,
    customerBilled,
    isProductChangeEligible,
    currentPlanCode,
    currentPlanTier,
    currentPlanFamily,
  ) => {
    if (!isAvailableOffersFetched) return false;
    if (!(getFromBrandConfig('upsellBanner') ?? true)) return false;
    if (!customerBilled || !isProductChangeEligible) return false;

    /* `checkUpSellPlanAvailable` (`product.service.ts:280-292`) — an available
       offer counts as an upgrade when it sits on a higher tier, or when it is
       the family edition of the tier the member is already on. */
    const currentRank = tierRank(currentPlanTier);

    return availableOffers.some((o) => {
      const offerRank = tierRank(o.plan?.tier);

      if (currentPlanCode !== o.plan?.code && currentRank < offerRank) {
        return true;
      }
      return (
        !currentPlanFamily && !!o.plan?.family && currentRank === offerRank
      );
    });
  },
);

/**
 * "One plan. Total peace of mind." — Ultimate Individual upgrade banner. Ports
 * Angular `shouldShowUltimate` (`dashboard-carousel.component.ts:214-222`): the
 * member is on an individual, non-Ultimate plan and an individual Ultimate
 * product is available to switch to.
 */
export const selectIsEligibleForUltimateIndividualBanner = createSelector(
  [
    selectSatisfiesUpgradeBannerOfferGate,
    selectIsProductChangeEligible,
    selectIsAvailableOffersFetched,
    selectAvailableOffers,
    selectCurrentPlanFamily,
    selectOfferPlanTier,
  ],
  (
    satisfiesOfferGate,
    isProductChangeEligible,
    isAvailableOffersFetched,
    availableOffers,
    currentPlanFamily,
    currentPlanTier,
  ) => {
    if (!isAvailableOffersFetched) return false;
    if (!getUpgradeBannerConfig().ultimate) return false;
    if (!satisfiesOfferGate || !isProductChangeEligible) return false;
    if (currentPlanFamily) return false;
    if (isUltimateTier(currentPlanTier)) return false;

    return availableOffers.some(
      (o) => isUltimateTier(o.plan?.tier) && !o.plan?.family,
    );
  },
);

/**
 * "One plan. Peace of mind for the entire family." — Ultimate Family upgrade
 * banner. Ports Angular `shouldShowUltimateFamily`
 * (`dashboard-carousel.component.ts:243-255`).
 *
 * The current-plan test is deliberately either/or: an Ultimate member on an
 * individual plan, or a family member below the Ultimate tier. A family member
 * already on Ultimate has nothing to upgrade to and is excluded.
 */
export const selectIsEligibleForUltimateFamilyBanner = createSelector(
  [
    selectSatisfiesUpgradeBannerOfferGate,
    selectIsProductChangeEligible,
    selectIsAvailableOffersFetched,
    selectAvailableOffers,
    selectCurrentPlanFamily,
    selectOfferPlanTier,
  ],
  (
    satisfiesOfferGate,
    isProductChangeEligible,
    isAvailableOffersFetched,
    availableOffers,
    currentPlanFamily,
    currentPlanTier,
  ) => {
    if (!isAvailableOffersFetched) return false;
    if (!getUpgradeBannerConfig().ultimate) return false;
    if (!satisfiesOfferGate || !isProductChangeEligible) return false;

    const currentIsUltimate = isUltimateTier(currentPlanTier);
    const qualifies =
      (currentIsUltimate && !currentPlanFamily) ||
      (!currentIsUltimate && currentPlanFamily);
    if (!qualifies) return false;

    return availableOffers.some(
      (o) => isUltimateTier(o.plan?.tier) && !!o.plan?.family,
    );
  },
);

/**
 * "Protect your entire family" — Family upgrade banner. Ports Angular
 * `shouldShowFamily` (`dashboard-carousel.component.ts:257-265`). Carried by
 * its own `planUpgradeBanners.family` flag because Angular gives this banner to
 * a narrower set of brands than the two Ultimate ones (`:152` vs `:134`).
 *
 * Distinct from `selectIsEligibleForCtpFamilyUpsellBanner`: that is the
 * breach/complimentary CTP path keyed on a `ctp upsell family` offer type, this
 * is the retail plan-change path keyed on an available non-Ultimate family
 * plan. They cannot both fire for the same member.
 */
export const selectIsEligibleForFamilyBanner = createSelector(
  [
    selectSatisfiesUpgradeBannerOfferGate,
    selectIsProductChangeEligible,
    selectIsAvailableOffersFetched,
    selectAvailableOffers,
    selectCurrentPlanFamily,
  ],
  (
    satisfiesOfferGate,
    isProductChangeEligible,
    isAvailableOffersFetched,
    availableOffers,
    currentPlanFamily,
  ) => {
    if (!isAvailableOffersFetched) return false;
    if (!getUpgradeBannerConfig().family) return false;
    if (!satisfiesOfferGate || !isProductChangeEligible) return false;
    if (currentPlanFamily) return false;

    return availableOffers.some(
      (o) => !isUltimateTier(o.plan?.tier) && !!o.plan?.family,
    );
  },
);

/**
 * Header diamond-icon "family upsell" notification gating — ports
 * Angular's `icon-notifications.component.ts:138-149`.
 *
 * NOTE: intentionally broader than
 * `selectIsEligibleForCtpFamilyUpsellBanner`. The dashboard banner
 * only shows for breach-comp users past the 90-day setup window;
 * Angular's diamond-icon shows whenever product-change is eligible
 * (or in one of two specific reason-code states) AND a `'ctp upsell
 * family'` offer is available — regardless of brand / billing type
 * / account age. Using the banner selector here would silently hide
 * the diamond for users Angular actively shows it to.
 *
 * Fires when BOTH conditions hold:
 *   • conversionEligibility.eligible === true, OR its reasonCode is
 *     'NOT_WITHIN_ELIGIBILITY_PERIOD' or
 *     'BREACH_COMPLIMENTARY_LIMITED_OFFER_CHANGE_SCHEDULED'.
 *   • some available offer has offerChangeConfiguration.type ===
 *     'ctp upsell family'.
 */
const CTP_FAMILY_UPSELL_NOTIFICATION_REASON_CODES = new Set([
  'NOT_WITHIN_ELIGIBILITY_PERIOD',
  'BREACH_COMPLIMENTARY_LIMITED_OFFER_CHANGE_SCHEDULED',
]);

export const selectIsEligibleForCtpFamilyUpsellNotification = createSelector(
  [
    selectConversionEligibility,
    selectAvailableOffers,
    selectIsAvailableOffersFetched,
  ],
  (conversionEligibility, availableOffers, isAvailableOffersFetched) => {
    if (!isAvailableOffersFetched) return false;
    const eligibilityOk =
      Boolean(conversionEligibility?.eligible) ||
      CTP_FAMILY_UPSELL_NOTIFICATION_REASON_CODES.has(
        conversionEligibility?.reasonCode ?? '',
      );
    if (!eligibilityOk) return false;
    return availableOffers.some(
      (o) =>
        (o.offerChangeConfiguration?.type ?? '').toLowerCase() ===
        'ctp upsell family',
    );
  },
);

export const selectIsEligibleForFinishAccountSetup = createSelector(
  [
    selectCompanyType,
    selectIsBillingAccountDetailsExist,
    selectBillingAccountDetails,
    selectIsEligibleForCtp,
  ],
  (
    companyType,
    isBillingAccountDetailsExist,
    billingAccountInfo,
    isEligibleForCtp,
  ) =>
    !!getFromBrandConfig('finishSetup') &&
    companyType === 'breach' &&
    isBillingAccountDetailsExist &&
    !billingAccountInfo?.defaultPaymentMethodId &&
    !isEligibleForCtp,
);

export const selectSubscriptionDetails = createSelector(
  [selectCtpState],
  (ctpState) => ctpState.subscriptionInfo,
);

export const selectIsSubscriptionDetailsExist = createSelector(
  [selectSubscriptionDetails],
  (subscriptionInfo) => subscriptionInfo !== null,
);

/**
 * The Card Theft Protection companion subscription, or `null`.
 *
 * Angular finds it exactly this way — `billing-term.component.ts:204` scans
 * `additionalSubscriptions` for `planCode === 'crdtftprt'`.
 *
 * What is **not** reproduced is the guard it then renders behind:
 * `additionalSubscription && additionalSubscription.length > 0`
 * (`billing-term.component.html:22,174`). `.find()` returns an object, so
 * `.length` is `undefined` and `undefined > 0` is false — the second-plan rows
 * never render in Angular at all. Consumers here test truthiness instead, which
 * is why this card appears where Angular's never did.
 */
export const selectCompanionSubscription = createSelector(
  [selectSubscriptionDetails],
  (subscriptionInfo) =>
    subscriptionInfo?.additionalSubscriptions?.find(
      (sub) => sub.planCode === COMPANION_PLAN_CODE,
    ) ?? null,
);

/**
 * Whether the member's current offer is one of the Privacy Assist bundle plans
 * — Angular's `op.plan.code === 'pastpctpin' || 'pastctpind'`
 * (`billing-term.component.html:167`), the other half of the companion gate.
 */
export const selectIsPrivacyAssistBundlePlan = createSelector(
  [selectOfferPlanCode],
  (planCode) => PRIVACY_ASSIST_PLAN_CODES.includes(planCode ?? ''),
);

export const selectIsEligibleForCtpOptIn = createSelector(
  [
    selectCompanyType,
    selectIsBillingAccountDetailsExist,
    selectBillingAccountDetails,
    selectIsEligibleForCtp,
    selectAvailableCtpPlan,
    selectConversionEligibility,
  ],
  (
    companyType,
    isBillingAccountDetailsExist,
    billingAccountInfo,
    isEligibleForCtp,
    availableCtpPlan,
    conversionEligibility,
  ) =>
    isEligibleForCtp ||
    (!!getFromBrandConfig('finishSetup') &&
      companyType === 'breach' &&
      !!availableCtpPlan &&
      isBillingAccountDetailsExist &&
      (!billingAccountInfo?.defaultPaymentMethodId ||
        (!billingAccountInfo?.autoRenew &&
          conversionEligibility?.reasonCode !==
            CTP_ELGIBILITY_DETAILS.pendingCtp))),
);

export const selectSubscriptionCreditBalanceCents = createSelector(
  [selectSubscriptionDetails],
  (subscriptionInfo) =>
    subscriptionInfo?.currentPeriodEndsAt
      ? (subscriptionInfo.creditBalance ?? 0)
      : 0,
);
